import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { filter } from 'rxjs/operators';
import { gsap } from 'gsap';
declare let $: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [
        Location, {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        }
    ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    title: any;
    location: any;
    routerSubscription: any;
    private imageHoverRoot?: HTMLElement;
    private hasNavigatedOnce = false;
    private cancelReloadScroll = false;

    constructor(private router: Router) {
    }

    ngOnInit(){
        this.recallJsFuntions();
    }

    ngAfterViewInit(): void {
        this.enableImageHoverEffects();
        this.restoreThenAnimateScrollOnReload();
    }

    /* AppComponent is only ever constructed once per full page load (a browser reload
       destroys and recreates the whole Angular app), and only ever once — SPA route
       changes via the router re-render inside <router-outlet> without re-running this. So
       this fires exactly on reload, not on every in-app navigation.

       index.html (part 1) disables the browser's own scroll restoration and continuously
       saves the scroll position to sessionStorage before unload — but deliberately does
       NOT jump back to that saved position itself, because at that point (before <body>
       is even parsed) the document has no real height yet, so scrollTo() would have
       nothing to scroll the page through and would silently clamp to 0. That was the
       actual bug in the previous version of this feature: the "restore" never did
       anything, so by the time this method ran window.scrollY was already 0, and the
       (correct, but now moot) guard against animating "0 to 0" meant nothing ever visibly
       happened.

       This is part 2: by ngAfterViewInit the routed page's own template is in the DOM, but
       the WordPress-driven sections under it may still be showing loading skeletons, so
       the page might not yet be as tall as it will eventually be. Polling frame-by-frame
       (not a blind timeout) until the document is tall enough to actually contain the
       saved position — or a bounded number of frames pass — means the instant jump lands
       as close to the real remembered spot as what's actually renderable right now. */
    private restoreThenAnimateScrollOnReload(): void {
        const isReload = this.isBrowserReload();
        if (!isReload) {
            return;
        }

        let targetY = 0;
        try {
            targetY = parseInt(sessionStorage.getItem('blink:lastScrollY') || '0', 10) || 0;
        } catch {
            targetY = 0;
        }

        if (targetY <= 0) {
            return;
        }

        // Polls on a short setTimeout rather than requestAnimationFrame: rAF is throttled
        // or fully suspended by the browser for backgrounded/non-visible tabs, and this
        // needs to reliably still run even if a reload happens to land in that state
        // briefly. setTimeout has no such dependency. 16ms mirrors a 60fps frame interval —
        // this is a short, bounded, repeatedly-checked poll against actual rendered page
        // height, not a blind "wait N seconds and hope" timeout.
        const pollIntervalMs = 16;
        const maxAttempts = 30; // ~480ms ceiling
        let attempt = 0;

        const attemptRestore = (): void => {
            if (this.cancelReloadScroll) {
                return;
            }
            attempt++;
            const maxScrollable = document.documentElement.scrollHeight - window.innerHeight;

            if (maxScrollable >= targetY || attempt >= maxAttempts) {
                const jumpTo = Math.min(targetY, Math.max(maxScrollable, 0));
                // Must be the options-object form with an explicit behavior: 'auto' — the
                // legacy two-argument scrollTo(x, y) has no way to specify behavior, so it
                // falls back to the computed scroll-behavior CSS property (per the CSSOM
                // View spec). Bootstrap's reboot CSS sets scroll-behavior: smooth globally
                // on <html> (see the back-to-top button's own handler in custom.js, which
                // already works around this same issue), so a plain scrollTo(0, jumpTo)
                // here would ALSO animate smoothly — starting an animation toward jumpTo
                // that the smooth-to-0 call below would then interrupt moments later,
                // before it ever visibly got there. This has to be a genuinely instant jump.
                this.jumpToScrollPosition(jumpTo);

                // A separate tick between the instant jump and the smooth-scroll start
                // ensures the browser actually paints the restored position first — firing
                // both scrollTo calls back to back risks them being coalesced into one
                // visual update, which would skip straight to 0 with nothing ever visibly
                // shown at the restored position in between.
                setTimeout(() => {
                    if (this.cancelReloadScroll) {
                        return;
                    }
                    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    window.scrollTo({ top: 0, left: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
                }, pollIntervalMs);
                return;
            }

            setTimeout(attemptRestore, pollIntervalMs);
        };

        setTimeout(attemptRestore, pollIntervalMs);
    }

    // Bootstrap enables smooth scrolling at the HTML level. A scrollTo()
    // request with behavior:'auto' would inherit that and animate unexpectedly.
    // Temporarily disable the CSS preference for true instant navigation jumps.
    private jumpToScrollPosition(top: number): void {
        const html = document.documentElement;
        const previousBehavior = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, top);
        html.style.scrollBehavior = previousBehavior;
    }

    private isBrowserReload(): boolean {
        const [navigationEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (navigationEntry) {
            return navigationEntry.type === 'reload';
        }

        const legacyNavigation = (performance as any).navigation;
        return !!legacyNavigation && legacyNavigation.type === legacyNavigation.TYPE_RELOAD;
    }

    ngOnDestroy(): void {
        this.cancelReloadScroll = true;
        this.imageHoverRoot?.removeEventListener('mouseover', this.handleImageMouseOver);
        this.imageHoverRoot?.removeEventListener('mouseout', this.handleImageMouseOut);
    }

    private enableImageHoverEffects(): void {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.matchMedia('(hover: hover)').matches) {
            return;
        }

        this.imageHoverRoot = document.querySelector('app-root') || undefined;
        this.imageHoverRoot?.addEventListener('mouseover', this.handleImageMouseOver);
        this.imageHoverRoot?.addEventListener('mouseout', this.handleImageMouseOut);
    }

    private handleImageMouseOver = (event: MouseEvent): void => {
        const image = event.target instanceof HTMLImageElement ? event.target : null;
        if (!this.isHoverableImage(image)) {
            return;
        }

        gsap.to(image, {
            scale: 1.025,
            y: -2,
            duration: 0.3,
            ease: 'power2.out',
            overwrite: 'auto'
        });
    };

    private handleImageMouseOut = (event: MouseEvent): void => {
        const image = event.target instanceof HTMLImageElement ? event.target : null;
        if (!this.isHoverableImage(image)) {
            return;
        }

        gsap.to(image, {
            scale: 1,
            y: 0,
            duration: 0.45,
            ease: 'power3.out',
            overwrite: 'auto'
        });
    };

    private isHoverableImage(image: HTMLImageElement | null): image is HTMLImageElement {
        return !!image &&
            // These components already animate their images with CSS or Owl.
            // GSAP would write an inline transform over the existing animation.
            !image.closest('app-navbar, app-footer, app-preloader, .video-side, .work-item, .owl-carousel, .banner-slider') &&
            !image.classList.contains('no-image-hover');
    }

    recallJsFuntions() {
        this.routerSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(event => {
            this.location = this.router.url;
            // Re-apply UI plugins to newly routed DOM without reloading the
            // whole script and attaching the same event handlers repeatedly.
            requestAnimationFrame(() => (window as any).blinkInitUi?.());
            // Skip the very first NavigationEnd — that's the initial route resolving as
            // part of app bootstrap (including on every browser reload), not a real in-app
            // navigation. Instantly snapping to 0 here would wipe out the scroll position
            // the reload-smooth-scroll feature (ngAfterViewInit below) needs to still be
            // there to animate FROM, before it even gets a chance to run. Every subsequent
            // NavigationEnd (an actual link click / route change) is completely unaffected
            // and still resets scroll instantly, exactly as before.
            if (!this.hasNavigatedOnce) {
                this.hasNavigatedOnce = true;
                return;
            }
            this.cancelReloadScroll = true;
            this.jumpToScrollPosition(0);
        });
    }
}
